import type { ILookerConnection, PageChangedEvent } from '@looker/embed-sdk';
import isEqual from 'lodash/isEqual';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { buildFiltersQueryString } from '../helpers';
import { useCrossFilters } from '../hooks/useCrossFilters';
import { useCurrentWorkspace } from '../hooks/useCurrentWorkspace';
import { useFilterConfigs } from '../hooks/useFilterConfigs';
import { useFilters } from '../hooks/useFilters';
import { getDashboardBaseSections } from '../hooks/useLooker';
import { fetchDashboardFilters } from '../lib/api/looker/api';
import type { DashboardFilter } from '../lib/api/looker/dto';
import { filterDependencies } from '../modules/GenomeWorkspaceView/LeftView/components/FilterDependencies';
import type { ExpressionType, Filter } from '../stores/filters';

interface ILookerDashboardContext {
  reloadDashboard: (filters: Filter[]) => void;
  getCombinedFiltersArray: () => Filter[];
  connection: ILookerConnection | null;
  setConnection: (dashboard: ILookerConnection | null) => void;
  updateDashboard: () => void;
  dashboardFilters?: DashboardFilter[];
  dashboardFiltersLoading?: boolean;
  dashboardFiltersError?: Error | null;
  catchCrossFilters: (event: PageChangedEvent) => void;
  triggerReset: () => void;
  triggerUpdate: () => void;
  resetTriggered: boolean;
  updateTriggered: boolean;
  setResetTriggered: (value: boolean) => void;
  isUpdating: boolean;
  setIsUpdating: (value: boolean) => void;
}

interface LookerDashboardProviderProps extends PropsWithChildren {
  disableAsync?: boolean;
}

const LookerDashboardContext = createContext<ILookerDashboardContext>({
  reloadDashboard: () => null,
  getCombinedFiltersArray: () => [],
  connection: null,
  setConnection: () => null,
  updateDashboard: () => null,
  catchCrossFilters: () => null,
  dashboardFilters: [],
  triggerReset: () => {},
  triggerUpdate: () => {},
  resetTriggered: false,
  updateTriggered: false,
  setResetTriggered: () => {},
  isUpdating: false,
  setIsUpdating: () => {},
});

export const LookerDashboardProvider: FC<LookerDashboardProviderProps> = ({ children, disableAsync = false }) => {
  const [connection, setConnection] = useState<ILookerConnection | null>(null);
  const [dashboardFilters, setDashboardFilters] = useState<DashboardFilter[]>([]);
  const [dashboardFiltersLoading, setDashboardFiltersLoading] = useState(false);
  const [dashboardFiltersError, setDashboardFiltersError] = useState<Error | null>(null);
  const [resetTriggered, setResetTriggered] = useState(false);
  const [updateTriggered, setUpdateTriggered] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const lastAppliedFiltersRef = useRef<Filter[] | null>(null);

  const { filters, setMultipleFilters } = useFilters();
  const { allActiveCrossFilters, setDecodedFilterKeys, setCrossFilter, clearCrossFilters, filterKeys } =
    useCrossFilters();
  const { currentWorkspace, lookerDashboardID, currentWorkspaceId } = useCurrentWorkspace();

  const { filterConfigs } = useFilterConfigs({
    workspaceTypeId: currentWorkspace?.workspace_type_id,
    disableAutoFetch: !currentWorkspace?.workspace_type_id,
  });

  const filtersList = useMemo(() => filters && (Object.values(filters) as Array<Filter>), [filters]);

  useEffect(() => {
    if (!lookerDashboardID) return;
    lastAppliedFiltersRef.current = filtersList;
  }, [currentWorkspaceId, lookerDashboardID]);

  const loadDashboardFilters = async () => {
    if (!isMountedRef.current) return;

    setDashboardFiltersLoading(true);
    setDashboardFiltersError(null);
    try {
      const dashboardBaseObjects = getDashboardBaseSections(currentWorkspace?.workspace_type_id ?? 0);

      const dashboardSlugId = dashboardBaseObjects[0];

      const dashboardFilters = await fetchDashboardFilters(dashboardSlugId);

      if (isMountedRef.current) {
        setDashboardFilters(dashboardFilters);
      }
    } catch (error) {
      console.error(error);
      if (isMountedRef.current) {
        setDashboardFiltersError(error as Error);
      }
    } finally {
      if (isMountedRef.current) {
        setDashboardFiltersLoading(false);
      }
    }
  };

  const getCrossFiltersArray = () => {
    const crossFilters: Filter[] = [];
    const filtersToDispatch: Filter[] = [];

    allActiveCrossFilters.forEach((crossFilter) => {
      const filterName = filterConfigs.find(
        ({ correspondingCrossFilter }) => correspondingCrossFilter === crossFilter.filterName,
      )?.filterName;

      if (!filterName) return;

      const filterData: Filter = {
        filterName,
        expression: String(crossFilter.expression),
        enabled: !!crossFilter.expression,
        expressionType: 'target' as ExpressionType,
      };

      crossFilters.push(filterData);
      filtersToDispatch.push(filterData);

      const filterItem = filterDependencies.find((dependency) => dependency.sourceFilter === filterName);
      if (!filterItem?.targetFilter?.length) return;

      const targetFilters = Array.isArray(filterItem.targetFilter)
        ? filterItem.targetFilter
        : [filterItem.targetFilter];

      targetFilters.forEach((requiredFilterName) => {
        const existingFilter = filtersList.find((filter) => filter.filterName === requiredFilterName);

        const dashboardFilterExpression = dashboardFilters.find(
          (dashboardFilter) => dashboardFilter.name === requiredFilterName,
        )?.default_value;

        let expression = existingFilter?.expression || dashboardFilterExpression;

        if (requiredFilterName.toLowerCase().includes('events')) {
          expression = 'Yes';
        }

        if (!expression) return;

        const requiredFilterData: Filter = {
          filterName: requiredFilterName,
          expression,
          enabled: true,
          expressionType: 'target' as ExpressionType,
        };

        filtersToDispatch.push(requiredFilterData);
      });
    });

    setMultipleFilters(filtersToDispatch);

    return filtersToDispatch;
  };

  const getCombinedFiltersArray = () => {
    const crossFilters = getCrossFiltersArray();
    const crossFilterNamesSet = new Set(crossFilters.map((cf) => cf.filterName));

    return [...filtersList.filter((filter) => !crossFilterNamesSet.has(filter.filterName)), ...crossFilters];
  };

  const updateDashboard = async () => {
    let filtersToUpdate = [...filtersList];

    if (!(allActiveCrossFilters?.length && filtersList)) {
      await reloadDashboard(filtersToUpdate);
      triggerUpdate();
      return;
    }

    filtersToUpdate = getCombinedFiltersArray();

    clearCrossFilters();

    await reloadDashboard(filtersToUpdate);
    triggerUpdate();
  };

  const reloadDashboard = async (filtersToUpdate: Filter[]) => {
    if (isEqual(filtersToUpdate, lastAppliedFiltersRef.current) && filterKeys.length === 0) {
      return;
    }

    setIsUpdating(true);
    void (await connection?.loadDashboard('reload-dashboard'));
    connection?.send('dashboard:stop');
    const url = buildFiltersQueryString(filtersToUpdate);
    void (await connection?.loadDashboard(`${lookerDashboardID}${url}`));

    lastAppliedFiltersRef.current = filtersToUpdate;
  };

  const triggerReset = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    const id = setTimeout(() => {
      setResetTriggered(false);
      timeoutIdRef.current = null;
    }, 100);
    timeoutIdRef.current = id;
  };

  const triggerUpdate = () => {
    setUpdateTriggered(true);

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    const id = setTimeout(() => {
      setUpdateTriggered(false);
      timeoutIdRef.current = null;
    }, 100);
    timeoutIdRef.current = id;
  };

  const decodeURLSearchParams = (queryParams: URLSearchParams): { [key: string]: string } => {
    const decodedFilters: { [key: string]: string } = {};
    queryParams.forEach((value, key) => {
      if (key.startsWith('tile')) {
        const decodedKey = key.split('.').slice(1).join('.');
        const decodedValue = decodeURIComponent(value).replace(/"/g, '');
        decodedFilters[decodedKey] = decodedValue;
      }
    });

    return decodedFilters;
  };

  const catchCrossFilters = (event: PageChangedEvent) => {
    const queryParams = new URL(event.page.absoluteUrl).searchParams;
    const decodedFilters = decodeURLSearchParams(queryParams);
    const decodedFilterKeys = decodedFilters && Object.keys(decodedFilters);

    setDecodedFilterKeys({
      filterKeys: decodedFilterKeys,
    });

    const affectedFilters = decodedFilterKeys?.map((filter) =>
      filterConfigs.find((key) => key.correspondingCrossFilter === filter),
    );

    affectedFilters.forEach((crossfilter) => {
      if (crossfilter?.correspondingCrossFilter) {
        setCrossFilter({
          filterName: crossfilter.correspondingCrossFilter,
          expression: decodedFilters[crossfilter?.correspondingCrossFilter],
        });
      }
    });
  };

  useEffect(() => {
    if (!disableAsync) {
      void loadDashboardFilters();
    }
  }, [currentWorkspace?.workspace_type_id, disableAsync]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      reloadDashboard,
      getCombinedFiltersArray,
      connection,
      setConnection,
      updateDashboard,
      dashboardFilters,
      dashboardFiltersLoading,
      dashboardFiltersError,
      catchCrossFilters,
      triggerReset,
      triggerUpdate,
      resetTriggered,
      updateTriggered,
      setResetTriggered,
      isUpdating,
      setIsUpdating,
    }),
    [
      connection,
      dashboardFilters,
      dashboardFiltersLoading,
      dashboardFiltersError,
      resetTriggered,
      updateTriggered,
      isUpdating,
      filters,
      filterKeys,
      allActiveCrossFilters,
      // Functions are stable because they're defined once per component instance
      // and their closures capture stable refs
    ],
  );

  return <LookerDashboardContext.Provider value={contextValue}>{children}</LookerDashboardContext.Provider>;
};

export const useLookerDashboardContext = () => {
  return useContext<ILookerDashboardContext>(LookerDashboardContext);
};
